<template>
  <DeviceSetupSection>
    <template v-slot:left>
      <PageContainer
        :class="$style.container"
      >
        <template v-slot:body>
          <Container>
            <HeadingLarge>
              {{ settings.label }}
            </HeadingLarge>
            <ParagraphBody bodySize="large">
              {{ subheader }}
            </ParagraphBody>
            <div
              v-for="option in settings.installOptions"
              :key="option.value"
              :class="[$style.btn, {
                [$style.selected]: option.value === selectedInstall.value
              }]"
              @click="handleSelection(option)"
            >
              <ParagraphBody bodySize="large">
                {{ option.label }}
              </ParagraphBody>
            </div>
          </Container>
        </template>
        <template v-slot:footer>
          <Container>
            <ButtonGroupSave
              :isDisabledPrimary="!selectedInstall"
              :isFullWidth="false"
              :primaryButtonProps="$options.primaryButtonProps"
              :secondaryButtonProps="$options.secondaryButtonProps"
              @primaryAction="handleNext"
              @secondaryAction="handleBack"
            />
          </Container>
        </template>
      </PageContainer>
    </template>
    <template v-slot:right>
      <div
        :class="[$style.image, {
          [$style.center]: !selectedInstall && selectedOs === mac
        }]"
        :style="{ backgroundImage }"
      />
    </template>
  </DeviceSetupSection>
</template>

<script>
import * as Constants from 'client/util/Constants';
import {
  ButtonGroupSave,
  Container,
  HeadingLarge,
  PageContainer,
  ParagraphBody,
  Types,
} from '@jumpcloud/ui-components';
import {
  eventLocations,
  eventProperties,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { guidedSetupConfig } from 'client/Discover/components/GuidedSetup/GuidedSetupSettings';
import DeviceSetupSection from 'client/Discover/components/GuidedSetup/DeviceSetupSection.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';

const { mac } = Constants.default;
const { PrimaryButtonProps, SecondaryButtonProps } = Types;
const primaryButtonProps = PrimaryButtonProps({ text: 'Setup Device' });
const secondaryButtonProps = SecondaryButtonProps({ hasNoBackground: true, text: 'Back' });

const {
  description,
  location,
  section,
  singleSelectSubmitted,
  text,
} = eventProperties;

export default {
  name: 'InstallOptions',

  components: {
    ButtonGroupSave,
    Container,
    DeviceSetupSection,
    HeadingLarge,
    PageContainer,
    ParagraphBody,
  },

  EventHelper,

  primaryButtonProps,
  secondaryButtonProps,

  props: {
    selectedOs: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      mac,
      selectedInstall: '',
    };
  },

  computed: {
    backgroundImage() {
      return this.selectedInstall && this.selectedInstall.images
        ? this.selectedInstall.images.default : this.settings.setupImage;
    },

    settings() {
      return guidedSetupConfig[this.selectedOs];
    },

    subheader() {
      return `How do you want to set up your ${this.settings.label} device?`;
    },
  },

  methods: {
    handleBack() {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'User went back a step',
        [location]: eventLocations.immersiveOnboarding,
        [section]: `Select Setup Method - ${this.settings.label}`,
        [text]: 'Back',
      });

      // When going back from this step, reset selectedInstall and selectedOs
      this.selectInstall({});
      this.$emit('onOsSelection', '');
      this.$emit('back');
    },

    handleNext() {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'User proceeded through the guided setup flow',
        [location]: eventLocations.immersiveOnboarding,
        [section]: `Select Setup Method - ${this.settings.label}`,
        [text]: 'Setup Device',
      });
      this.$emit('next');
    },

    handleSelection(option) {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: `User selected how to set up their ${this.settings.label} device`,
        [location]: eventLocations.immersiveOnboarding,
        [section]: `Select Setup Method - ${this.settings.label}`,
        [singleSelectSubmitted]: option.label,
        [text]: option.label,
      });

      this.selectInstall(option);
    },

    selectInstall(option) {
      this.selectedInstall = option;
      this.$emit('onInstallSelection', option);
    },
  },
};
</script>

<style scoped module>
@import './css/guided-setup.css';
</style>
